// MatrixChartTab — upload + display the client's official family/entity chart
// (PDF or image). Lives as a third tab inside ClientEntitiesHub alongside the
// list view and the auto-generated matrix.
import { useEffect, useState, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Upload, FileText, Image as ImageIcon, Trash2, Loader2,
  Download, AlertCircle, Clock, History, Sparkles, Check, X, RefreshCw,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_MIME = [
  'application/pdf', 'image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml',
];
const ALLOWED_EXT_LABEL = 'PDF, PNG, JPG, WEBP, GIF, SVG';

function fmtDate(iso, fmt = "d MMM yyyy 'at' h:mm a") {
  if (!iso) return '—';
  try { return format(parseISO(iso), fmt); } catch { return iso; }
}

function humanSize(bytes) {
  if (!bytes) return '—';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export default function MatrixChartTab() {
  const { clientId, client } = useOutletContext();
  const { token } = useAuth();
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [notes, setNotes] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const inputRef = useRef(null);
  const [fileUrl, setFileUrl] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token || !clientId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/clients/${clientId}/matrix-chart`, { headers });
      setChart(r.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setChart(null);
      } else {
        toast.error('Could not load chart');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, clientId]);

  useEffect(() => { load(); }, [load]);

  // Whenever chart changes, fetch a fresh blob URL we can embed (so we don't
  // expose the bearer token via <img src>).
  const [fileError, setFileError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let url;
    setFileError(false);
    (async () => {
      if (!chart) { setFileUrl(null); return; }
      try {
        const r = await axios.get(`${BACKEND_URL}/api/clients/${clientId}/matrix-chart/file`, {
          headers, responseType: 'blob',
        });
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setFileUrl(url);
      } catch {
        if (!cancelled) {
          setFileUrl(null);
          setFileError(true);
        }
      }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart?.id]);

  const handleFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_MIME.includes(f.type) && !/\.(pdf|png|jpe?g|webp|gif|svg)$/i.test(f.name)) {
      toast.error(`Unsupported file type. Allowed: ${ALLOWED_EXT_LABEL}.`);
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error('File too large. Maximum 20MB.');
      return;
    }
    submit(f);
  };

  const submit = async (file) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      if (notes.trim()) form.append('notes', notes.trim());
      await axios.post(`${BACKEND_URL}/api/clients/${clientId}/matrix-chart`, form, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Chart uploaded');
      setNotes('');
      if (inputRef.current) inputRef.current.value = '';
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/clients/${clientId}/matrix-chart`, { headers });
      toast.success('Chart removed');
      setChart(null);
      setConfirmDelete(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/clients/${clientId}/matrix-chart/history`, { headers });
      setHistory(r.data?.items || []);
      setShowHistory(true);
    } catch {
      toast.error('Could not load history');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20" data-testid="matrix-chart-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#4A3B32]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="matrix-chart-tab">
      {!chart ? (
        <EmptyUploader
          inputRef={inputRef}
          uploading={uploading}
          notes={notes}
          setNotes={setNotes}
          handleFilePick={handleFilePick}
          clientName={client?.name}
        />
      ) : (
        <ChartView
          chart={chart}
          fileUrl={fileUrl}
          fileError={fileError}
          uploading={uploading}
          confirmDelete={confirmDelete}
          setConfirmDelete={setConfirmDelete}
          inputRef={inputRef}
          handleFilePick={handleFilePick}
          handleDelete={handleDelete}
          loadHistory={loadHistory}
          history={history}
          showHistory={showHistory}
        />
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.svg"
        onChange={handleFilePick}
        style={{ display: 'none' }}
        data-testid="matrix-chart-file-input"
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────────

function EmptyUploader({ inputRef, uploading, notes, setNotes, handleFilePick, clientName }) {
  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) {
      const dt = new DataTransfer();
      dt.items.add(f);
      if (inputRef.current) {
        inputRef.current.files = dt.files;
        handleFilePick({ target: inputRef.current });
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-[#E6E0D9] p-6">
        <h2 className="font-heading text-xl text-[#2C2420]">Upload family / entity chart</h2>
        <p className="text-sm text-[#5D524D] mt-1">
          Drop in the chart your firm uses for <strong>{clientName}</strong> — usually exported from
          your estate plan, governance pack, or family constitution. PDF is best; PNG/JPG also fine.
          If you have a PowerPoint, just <em>File &rarr; Export as PDF</em> first.
        </p>
      </div>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="w-full border-2 border-dashed border-[#E6E0D9] hover:border-[#A86A3A] rounded-2xl bg-white p-12 flex flex-col items-center gap-3 transition-colors group"
        data-testid="matrix-chart-dropzone"
      >
        {uploading ? (
          <>
            <Loader2 className="w-10 h-10 text-[#A86A3A] animate-spin" />
            <p className="text-sm text-[#5D524D]">Uploading…</p>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-full bg-[#FFF8F0] flex items-center justify-center group-hover:bg-[#A86A3A]/10 transition-colors">
              <Upload className="w-6 h-6 text-[#A86A3A]" />
            </div>
            <p className="text-[#2C2420] font-medium">Click to choose a file, or drag and drop</p>
            <p className="text-xs text-[#8C8078]">{ALLOWED_EXT_LABEL} · max 20 MB</p>
          </>
        )}
      </button>

      <div className="bg-white rounded-2xl border border-[#E6E0D9] p-5">
        <label className="text-xs uppercase tracking-wider text-[#8C8078] mb-1.5 block">
          Notes (optional)
        </label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="e.g. Updated 2026 family chart — supersedes the version in the 2024 estate plan."
          data-testid="matrix-chart-notes-input"
        />
      </div>
    </div>
  );
}

function ChartView({
  chart, fileUrl, fileError, uploading, confirmDelete, setConfirmDelete,
  inputRef, handleFilePick: _handleFilePick, handleDelete, loadHistory, history, showHistory,
}) {
  const isPdf = chart.contentType === 'application/pdf' || /\.pdf$/i.test(chart.filename || '');
  const isImage = (chart.contentType || '').startsWith('image/');

  return (
    <div className="space-y-4" data-testid="matrix-chart-view">
      {/* Metadata header */}
      <div className="bg-white rounded-2xl border border-[#E6E0D9] p-5 flex items-start justify-between gap-3 flex-wrap" data-testid="matrix-chart-meta">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-lg bg-[#FFF8F0] flex items-center justify-center shrink-0">
            {isPdf ? <FileText className="w-5 h-5 text-[#A86A3A]" /> : <ImageIcon className="w-5 h-5 text-[#A86A3A]" />}
          </div>
          <div className="min-w-0">
            <p className="text-[#2C2420] font-medium truncate" data-testid="matrix-chart-filename">{chart.filename}</p>
            <p className="text-xs text-[#8C8078] flex items-center gap-2 flex-wrap mt-0.5">
              <Clock className="w-3 h-3" />
              Uploaded {fmtDate(chart.uploadedAt)} by {chart.uploadedByName}
              <span>·</span>
              <span>{humanSize(chart.size)}</span>
            </p>
            {chart.notes && <p className="text-xs text-[#5D524D] mt-1.5 italic">"{chart.notes}"</p>}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="gap-1.5"
            data-testid="matrix-chart-replace-btn"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Replace
          </Button>
          {fileUrl && (
            <a
              href={fileUrl}
              download={chart.filename}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[#E6E0D9] bg-white text-[#2C2420] hover:bg-[#F9F7F5] transition-colors"
              data-testid="matrix-chart-download-btn"
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </a>
          )}
          <Button variant="outline" size="sm" onClick={loadHistory} className="gap-1.5" data-testid="matrix-chart-history-btn">
            <History className="w-3.5 h-3.5" />
            History
          </Button>
          {confirmDelete ? (
            <>
              <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="matrix-chart-delete-confirm-btn">
                Confirm
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmDelete(true)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1.5"
              data-testid="matrix-chart-delete-btn"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Viewer */}
      <div className="bg-white rounded-2xl border border-[#E6E0D9] overflow-hidden" data-testid="matrix-chart-viewer">
        {fileError ? (
          <div className="flex flex-col items-center gap-2 p-12 text-center text-[#5D524D]">
            <AlertCircle className="w-6 h-6 text-red-500" />
            <p className="text-sm font-medium">Couldn't load the chart file.</p>
            <p className="text-xs text-[#8C8078] max-w-md">
              The file may have moved or the storage backend is unavailable.
              Try re-uploading using the <strong>Replace</strong> button above.
            </p>
          </div>
        ) : !fileUrl ? (
          <div className="flex flex-col items-center justify-center py-20 text-[#8C8078]">
            <Loader2 className="w-6 h-6 animate-spin mb-2" />
            <p className="text-sm">Loading chart…</p>
          </div>
        ) : isPdf ? (
          <object
            data={fileUrl}
            type="application/pdf"
            className="w-full"
            style={{ height: 'calc(100vh - 320px)', minHeight: '500px' }}
            aria-label={chart.filename}
          >
            <div className="flex flex-col items-center gap-2 p-8 text-center">
              <AlertCircle className="w-6 h-6 text-[#A86A3A]" />
              <p className="text-sm text-[#5D524D]">
                Your browser can't preview the PDF inline.{' '}
                <a href={fileUrl} download={chart.filename} className="text-[#A86A3A] underline">
                  Download it instead
                </a>
                .
              </p>
            </div>
          </object>
        ) : isImage ? (
          <div className="overflow-auto bg-[#FBFAF8] p-4 flex items-center justify-center" style={{ minHeight: '500px' }}>
            <img src={fileUrl} alt={chart.filename} className="max-w-full h-auto" data-testid="matrix-chart-img" />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 p-8 text-center text-[#5D524D]">
            <AlertCircle className="w-6 h-6 text-[#A86A3A]" />
            <p className="text-sm">Preview not available for this file type.</p>
            <a href={fileUrl} download={chart.filename} className="text-sm text-[#A86A3A] underline">
              Download {chart.filename}
            </a>
          </div>
        )}
      </div>

      {/* History */}
      {showHistory && (
        <div className="bg-white rounded-2xl border border-[#E6E0D9] p-5" data-testid="matrix-chart-history">
          <h3 className="font-heading text-base text-[#2C2420] mb-3">Earlier versions</h3>
          {history.length === 0 ? (
            <p className="text-sm text-[#8C8078]">No earlier versions on record.</p>
          ) : (
            <ul className="divide-y divide-[#F0EAE3]">
              {history.map((h) => (
                <li key={h.id} className="py-2.5 flex items-center justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <p className="text-[#2C2420] truncate">{h.filename}</p>
                    <p className="text-xs text-[#8C8078]">
                      Uploaded {fmtDate(h.uploadedAt)} by {h.uploadedByName} · {humanSize(h.size)}
                    </p>
                    {h.notes && <p className="text-xs text-[#5D524D] italic mt-0.5">"{h.notes}"</p>}
                  </div>
                  <span className="text-[10px] text-[#8C8078]">Superseded {fmtDate(h.supersededAt, 'd MMM')}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <ExtractionsPanel chartId={chart.id} />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// AI-extracted suggestions panel
// ──────────────────────────────────────────────────────────────────────────

function ExtractionsPanel({ chartId }) {
  const { clientId } = useOutletContext();
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token || !clientId) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/clients/${clientId}/matrix-chart/extractions?status=pending`,
        { headers },
      );
      setItems(r.data?.items || []);
    } catch (err) {
      if (err.response?.status !== 404) toast.error('Could not load AI suggestions');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, clientId]);

  useEffect(() => { load(); }, [load]);

  // Poll while the extraction is running and no items yet — give Gemini up
  // to ~90s to produce something. Once items appear OR we time out, stop.
  useEffect(() => {
    if (loading || items.length > 0) return;
    let cancelled = false;
    let attempts = 0;
    const timer = setInterval(async () => {
      if (cancelled) return;
      attempts += 1;
      if (attempts > 18) { clearInterval(timer); return; }  // ~90s
      try {
        const r = await axios.get(
          `${BACKEND_URL}/api/clients/${clientId}/matrix-chart/extractions?status=pending`,
          { headers },
        );
        if (r.data?.items?.length) {
          setItems(r.data.items);
          clearInterval(timer);
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => { cancelled = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, loading, chartId]);

  const decide = async (ext, decision) => {
    setActing(ext.id);
    try {
      await axios.post(
        `${BACKEND_URL}/api/clients/${clientId}/matrix-chart/extractions/${ext.id}/${decision}`,
        {},
        { headers },
      );
      toast.success(decision === 'accept' ? 'Added to client' : 'Suggestion dismissed');
      setItems((prev) => prev.filter((x) => x.id !== ext.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActing(null);
    }
  };

  const reRun = async () => {
    setRetrying(true);
    try {
      await axios.post(`${BACKEND_URL}/api/clients/${clientId}/matrix-chart/extract`, {}, { headers });
      toast.success('Re-running AI extraction…');
      setItems([]);
      setLoading(true);
      // The polling effect above will pick up new items
      setTimeout(() => setLoading(false), 600);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start extraction');
    } finally {
      setRetrying(false);
    }
  };

  const indCount = items.filter((i) => i.kind === 'individual').length;
  const entCount = items.filter((i) => i.kind === 'entity').length;
  const edgeCount = items.filter((i) => i.kind === 'ownership_edge').length;

  return (
    <div className="bg-white rounded-2xl border border-[#E6E0D9] p-5" data-testid="matrix-chart-extractions">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="font-heading text-base text-[#2C2420] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#A86A3A]" />
            AI suggestions from this chart
          </h3>
          <p className="text-xs text-[#8C8078] mt-0.5">
            Gemini read the chart and proposed these. Accept what's right; dismiss the rest.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={reRun} disabled={retrying} className="gap-1.5" data-testid="matrix-chart-rerun-btn">
          {retrying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Re-run extraction
        </Button>
      </div>

      {loading && items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-[#8C8078]">
          <Loader2 className="w-5 h-5 animate-spin mb-2" />
          <p className="text-sm">Reading the chart… first results usually appear within 30 seconds.</p>
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-[#8C8078] text-center py-6">
          No pending suggestions. Either everything has been actioned, or the chart didn't surface anything new.
        </p>
      ) : (
        <>
          <div className="text-xs text-[#5D524D] mb-3">
            {indCount > 0 && <span className="mr-3"><strong>{indCount}</strong> individual{indCount === 1 ? '' : 's'}</span>}
            {entCount > 0 && <span className="mr-3"><strong>{entCount}</strong> entit{entCount === 1 ? 'y' : 'ies'}</span>}
            {edgeCount > 0 && <span><strong>{edgeCount}</strong> ownership link{edgeCount === 1 ? '' : 's'}</span>}
          </div>
          <ul className="divide-y divide-[#F0EAE3]" data-testid="matrix-chart-extractions-list">
            {items.map((ext) => (
              <ExtractionRow key={ext.id} ext={ext} acting={acting === ext.id} onDecide={decide} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function ExtractionRow({ ext, acting, onDecide }) {
  const p = ext.payload || {};
  const kindLabel = ext.kind === 'individual' ? 'Individual' : ext.kind === 'entity' ? 'Entity' : 'Ownership link';
  const kindColor = ext.kind === 'individual'
    ? 'bg-[#FFF8F0] text-[#A86A3A] border-[#E6D9C4]'
    : ext.kind === 'entity'
    ? 'bg-[#F0EBE6] text-[#4A3B32] border-[#E6E0D9]'
    : 'bg-slate-50 text-slate-600 border-slate-200';

  const title = ext.kind === 'ownership_edge' ? `${p.from} → ${p.to}` : p.name;
  const sub = ext.kind === 'individual' ? p.role : ext.kind === 'entity' ? p.type : p.kind;

  return (
    <li className="py-3 flex items-start justify-between gap-3" data-testid={`extraction-row-${ext.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${kindColor}`}>
            {kindLabel}
          </span>
          <p className="text-[#2C2420] font-medium truncate">{title}</p>
          {sub && <span className="text-xs text-[#8C8078]">· {sub}</span>}
        </div>
        {p.notes && <p className="text-xs text-[#5D524D] mt-1 italic">"{p.notes}"</p>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button
          size="sm"
          variant="outline"
          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1"
          disabled={acting}
          onClick={() => onDecide(ext, 'accept')}
          data-testid={`extraction-accept-${ext.id}`}
        >
          {acting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5" /> Add</>}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-[#8C8078] hover:text-[#5D524D]"
          disabled={acting}
          onClick={() => onDecide(ext, 'reject')}
          data-testid={`extraction-reject-${ext.id}`}
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
    </li>
  );
}
